import { KeyboardEvent as ReactKeyboardEvent, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";

export interface MenuItem {
  label: string;
  onSelect?: () => void;
  /** A page on this site, opened in a new tab */
  to?: string;
}

interface ActionsMenuProps {
  /** Read by screen readers, e.g. "More actions for ISO 13485 readiness" */
  label: string;
  items: MenuItem[];
}

/** Roughly how big the menu is, to keep it on screen (its CSS min-width is 190px) */
const MENU_HEIGHT = 160;
const MENU_WIDTH = 200;
const EDGE = 8;

// A ⋮ button with a small menu. The menu is drawn at the end of <body>, so the table's scroll
// area can't clip it. Escape, a click elsewhere or scrolling closes it; arrow keys move between items.
export default function ActionsMenu({ label, items }: ActionsMenuProps) {
  const [position, setPosition] = useState<{ top?: number; bottom?: number; left?: number; right?: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const open = position !== null;

  const close = (returnFocus: boolean) => {
    setPosition(null);
    if (returnFocus) buttonRef.current?.focus();
  };

  const toggle = () => {
    if (open) return close(true);
    const rect = buttonRef.current!.getBoundingClientRect();
    // Lined up with the button's right edge, or its left edge when that would run off the screen
    const horizontal = rect.right >= MENU_WIDTH + EDGE
      ? { right: window.innerWidth - rect.right }
      : { left: Math.max(EDGE, rect.left) };
    setPosition(rect.bottom + MENU_HEIGHT > window.innerHeight
      ? { bottom: window.innerHeight - rect.top + 4, ...horizontal }
      : { top: rect.bottom + 4, ...horizontal });
  };

  useEffect(() => {
    if (!open) return;
    // preventScroll: a scroll would close the menu straight away (phones especially)
    menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus({ preventScroll: true });
    const onPointer = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!menuRef.current?.contains(target) && !buttonRef.current?.contains(target)) close(false);
    };
    const onMove = (e: Event) => { if (!menuRef.current?.contains(e.target as Node)) close(false); };
    document.addEventListener("mousedown", onPointer);
    window.addEventListener("resize", onMove);
    window.addEventListener("scroll", onMove, true);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      window.removeEventListener("resize", onMove);
      window.removeEventListener("scroll", onMove, true);
    };
  }, [open]);

  const onMenuKey = (e: ReactKeyboardEvent) => {
    const entries = Array.from(menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []);
    const index = entries.indexOf(document.activeElement as HTMLElement);
    if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); close(true); }
    else if (e.key === "ArrowDown") { e.preventDefault(); entries[(index + 1) % entries.length]?.focus({ preventScroll: true }); }
    else if (e.key === "ArrowUp") { e.preventDefault(); entries[(index - 1 + entries.length) % entries.length]?.focus({ preventScroll: true }); }
    else if (e.key === "Tab") close(false);
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className="wa-btn wa-menu-toggle"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        onClick={toggle}
      >
        <span aria-hidden="true">⋮</span>
      </button>
      {open && createPortal(
        <div ref={menuRef} role="menu" aria-label={label} className="wa-menu" style={position} onKeyDown={onMenuKey}>
          {items.map(item => item.to ? (
            <Link key={item.label} role="menuitem" to={item.to} target="_blank" rel="noreferrer" onClick={() => close(false)}>
              {item.label}
            </Link>
          ) : (
            <button key={item.label} type="button" role="menuitem" onClick={() => { close(false); item.onSelect?.(); }}>
              {item.label}
            </button>
          ))}
        </div>,
        document.body,
      )}
    </>
  );
}
